import React, { Component } from 'react';

import { Checkbox } from '@atlaskit/checkbox';

import { ReportBugsProvider } from '@qality/common-components/src/lib/ReportBugsProvider';
import InlineEdit from '@qality/common-components/src/molecules/inline-edit';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestExecutionStep, TestStepsView } from '@qality/common-model';
import { StorageOfUniqueComponents } from '@qality/common-service';
import { TestExecutionPageContext } from '@qality/common-state';
import {
  InputUtils,
  PasteAttachmentsUtils,
  StringUtils,
  Utils,
} from '@qality/common-utils';

import { ExecutionStatusSelect } from '../select/execution-status-select/ExecutionStatusSelect';
import TestCaseBoxAttachments from '../test-case-box/TestCaseBoxAttachments';
import { ContainerStyled } from './TestExecutionTableRow.styled';
import './_test-execution-details.scss';

type Props = {
  testStep: TestExecutionStep;
  view: TestStepsView;
  testStepIndex: number;
  onUpdateTestStep: (
    testStepIndex: number,
    testStep: TestExecutionStep
  ) => Promise<void>;
  onUpdateTestStepSelection: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  testCaseIssueKey: string;
  onAddBug: () => Promise<void>;
  pastedAttachments?: Array<File>;
};

type State = {
  isLoading: boolean;
  isHovered: boolean;
  isEditing: boolean;
  inlineEditKey: number;
  pastedAttachments?: Array<File>;
};

export default class TestExecutionTableRow extends Component<Props, State> {
  static contextType = TestExecutionPageContext;
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      isHovered: false,
      isEditing: false,
      inlineEditKey: 0,
    };
  }

  onUpdateAttachment = (testStep: TestExecutionStep): void => {
    const { onUpdateTestStep } = this.props;
    if (onUpdateTestStep) {
      onUpdateTestStep(this.props.testStepIndex, testStep);
    }
  };

  onLoading = (value: boolean): void => {
    this.setState({ isLoading: value });
  };

  handleUpdateTestStepStatus = (status: any): void => {
    const { onUpdateTestStep, testStepIndex, testStep } = this.props;
    testStep.status = status;
    testStep.isStatusUpdate = true;
    onUpdateTestStep(testStepIndex, testStep);
  };

  handleUpdateTestStepComment = (comment: string): void => {
    const commentWithEmptyLines = InputUtils.keepEmptyLines(comment);
    const { onUpdateTestStep, testStepIndex, testStep } = this.props;
    if (
      (!Utils.isNilOrEmpty(testStep.comment) ||
        !Utils.isNilOrEmpty(commentWithEmptyLines)) &&
      testStep.comment != commentWithEmptyLines
    ) {
      testStep.comment = commentWithEmptyLines;
      onUpdateTestStep(testStepIndex, testStep);
    }
    this.setState({
      inlineEditKey: this.state.inlineEditKey + 1,
    });
  };

  handleOnPaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    const isPasteEnabled = [
      FEATURE_FLAG.isPremium,
      this.context.canEdit,
      this.context.canCreateAttachment,
    ].every(Boolean);

    if (isPasteEnabled) {
      this.setState({
        pastedAttachments:
          PasteAttachmentsUtils.convertPastedFiledToAttachments(e),
      });
    }
  };

  handleMouseEnter = (): void => {
    this.setState({ isHovered: true });
  };

  handleMouseLeave = (): void => {
    this.setState({ isHovered: false });
  };

  startCommentEditing = (): void => {
    this.setState({ isEditing: true });
  };

  stopCommentEditing = (): void => {
    this.setState({ isEditing: false });
  };

  render(): JSX.Element {
    const { isDetailView } = this.props.view;

    const testCaseClassNameColumn =
      'column-test-case' + (isDetailView ? '-details' : '');
    const BugsTableCell = StorageOfUniqueComponents.get('BugsTableCell');
    // @ts-expect-error <problem with StorageOfUniqueComponents retuning 2 types>
    const TestExecutionColumnsWidth: Record<string, string> =
      StorageOfUniqueComponents.get('TestExecutionColumnsWidth');

    return (
      <ContainerStyled
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        className='test-execution-step-data-wrapper'
        onPaste={this.handleOnPaste}
      >
        <div className={'column-row ' + testCaseClassNameColumn}>
          <div className='test-case-box-step-id'>
            {this.props.testStepIndex + 1}.
          </div>
          <div>
            <p
              className='text-wrapper'
              dangerouslySetInnerHTML={{
                __html: StringUtils.toHTML(this.props.testStep.step),
              }}
            />
          </div>
        </div>

        {isDetailView && (
          <div className={'column-row ' + testCaseClassNameColumn}>
            <div>
              <p
                className='text-wrapper'
                dangerouslySetInnerHTML={{
                  __html: StringUtils.toHTML(this.props.testStep.data),
                }}
              />
            </div>
          </div>
        )}

        <div className={'column-row ' + testCaseClassNameColumn}>
          <div>
            <p
              className='text-wrapper'
              dangerouslySetInnerHTML={{
                __html: StringUtils.toHTML(this.props.testStep.result),
              }}
            />
          </div>
        </div>

        {isDetailView && (
          <div className={'column-row ' + testCaseClassNameColumn}>
            <TestCaseBoxAttachments
              testStep={this.props.testStep}
              canEdit={false}
            />
          </div>
        )}

        <div style={{ width: '2em' }} />

        <div
          className='column-row column-test-execution hide-focus-wrapper'
          style={{ width: TestExecutionColumnsWidth.commentColumn }}
        >
          <>
            {this.context.canEdit ? (
              <div
                onFocus={this.startCommentEditing}
                onBlur={this.stopCommentEditing}
              >
                <InlineEdit
                  key={this.state.inlineEditKey}
                  text={this.props.testStep.comment}
                  onConfirm={(value) => this.handleUpdateTestStepComment(value)}
                  length={5000}
                />
              </div>
            ) : (
              <p
                className='text-wrapper'
                dangerouslySetInnerHTML={{
                  __html: StringUtils.toHTML(this.props.testStep.comment),
                }}
              />
            )}
          </>
        </div>

        <div
          className='column-row column-test-execution'
          style={{
            width: TestExecutionColumnsWidth.attachementColumn,
          }}
        >
          <TestCaseBoxAttachments
            testStep={this.props.testStep}
            onUpdateAttachment={this.onUpdateAttachment}
            issueKey={this.props.testCaseIssueKey}
            onLoading={this.onLoading}
            loading={this.state.isLoading}
            canEdit={this.context.canEdit}
            canCreateAttachment={this.context.canCreateAttachment}
            canDeleteAllAttachment={this.context.canDeleteAllAttachment}
            canDeleteOwnAttachment={this.context.canDeleteOwnAttachment}
            currentUser={this.context.currentUser}
            propertyNameToStoreAttachments='executionAttachments'
            pastedAttachments={this.state.pastedAttachments}
            isHovered={this.state.isHovered}
          />
        </div>

        {BugsTableCell && (
          <div
            className='column-row column-test-execution bugs-cell'
            style={{ width: TestExecutionColumnsWidth.bugsColumn }}
          >
            <div>
              <ReportBugsProvider
                testExecutionStepId={this.props.testStep.testExecutionStepId}
                projectId={this.context.testCycle?.projectId}
                linkedIssues={this.props.testStep?.linkedIssues}
                onAddBug={this.props.onAddBug}
              >
                {/* @ts-expect-error <strange bug probably caused by StorageOfUniqueComponents or BugsTableCell implementation> */}
                <BugsTableCell
                  readOnly={!this.context.canEdit}
                  closePopup={this.state.isEditing}
                />
              </ReportBugsProvider>
            </div>
          </div>
        )}

        <div
          className='column-row column-test-execution'
          style={{ width: TestExecutionColumnsWidth.statusColumn }}
        >
          <ExecutionStatusSelect
            value={this.props.testStep.status}
            statuses={this.context.statuses.allStatuses}
            onChange={this.handleUpdateTestStepStatus}
            isDisabled={!this.context.canEdit}
          />
        </div>

        <div
          className='column-row column-checkbox-test-execution'
          style={{ width: TestExecutionColumnsWidth.checkboxColumn }}
        >
          <Checkbox
            isChecked={this.props.testStep.isSelected}
            onChange={this.props.onUpdateTestStepSelection}
            isDisabled={!this.context.canEdit}
          />
        </div>
      </ContainerStyled>
    );
  }
}
